/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { adminMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/adminMutationKeys";
import {
  Breadcrumbs,
  Divider,
  Alert,
  Snackbar,
  Box,
  InputAdornment,
 } from "@mui/material";
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import {
  StyledBreadCrumbContainer,
  StyledLink,
  StyledTitleContainer,
  StyledTitle,
  StyledSearchBar,
  StyledCreateButton,
  RestaurantAdminContainer,
  StyledTab,
  StyledTabPanel,
 } from "./styles";
import AddIcon from '@mui/icons-material/Add';
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { RestaurantAdminsTable } from "../../components/restaurant_admins_table";
import { AbilityContext } from '../../contexts/abilityContext'
import { strictCanScope } from "../../utils/caslStrict";
import { Loader } from "../../components/loader";
import { useInfiniteQuery, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useNotificationContext } from "../../contexts/notificationContext";
import { useSelector, useDispatch } from "react-redux";
import { fetchAdmin } from "../../store/adminSlice";
import { checkAdmin } from "../../utils/checkAdmin";
import dayjs from "dayjs";
import SearchIcon from '@mui/icons-material/Search';
import MfaConfigurationModal from '../../components/mfa_configuration_modal';
import { Query_Keys } from "../../lib/queryKeys";
import { usePermissionModules } from "../../hooks/usePermissionModules";
import { usePermissionProfiles } from "../../hooks/usePermissionProfiles";
import { NO_PROFILE_VALUE } from "../../components/permission_profile_picker";
import { RestaurantAdminModal } from "../../components/restaurant_admin_modal";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  buildModuleAccessSummary,
  filterProfilesForRoles,
} from "../../utils/permissionProfileSelection";
import { normalizeSearchTerm } from "../../utils/normalizeSearchTerm";
import { useDebounce } from "../../utils/useDebounce";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { PerPage } from "../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery";

let id = 100

const EMPTY_UNAVAILABLE_MODULES = Object.freeze({})

export const UserManagement = () => {
  const { admin } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const flattenDropdownPages = (data) => data.pages.flat();

  const { mutateAsync: createAdminMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/admins', payload),
    invalidateKeys: adminMutationKeyGroups.create,
    onSuccess: () => {
      setOpen(false)
      setSuccessOpen(true)
      setStatus(false)
    }
  });

  const { mutateAsync: updateAdminMutation } = useOtMutation({
    mutationFn: async ({ id, payload }) => await api.put(`/v1/admin/admins/${id}`, payload),
    invalidateKeys: adminMutationKeyGroups.update,
    onSuccess: (data, variables) => {
      setOpenEdit(false)
      setEditSuccess(true)
      setStatus(false)
      setInstanceRole([])
      
      if (admin && variables.id === admin.id) {
        dispatch(fetchAdmin());
      }
    }
  });

  const { mutateAsync: updateBulkMfaMutation } = useOtMutation({
    mutationFn: async (payload) => {
      if (typeof payload.enabled !== 'boolean') {
        throw new Error('enabled must be a boolean');
      }
      if (payload.enforcement && !['mandatory', 'optional'].includes(payload.enforcement)) {
        throw new Error('enforcement must be mandatory or optional');
      }
      return await api.post('/v1/admin/admins/mfa/bulk_toggle_enabled', {
        mfa: {
          enabled: payload.enabled,
          ...(payload.enforcement ? { enforcement: payload.enforcement } : {}),
        },
      });
    },
    invalidateKeys: adminMutationKeyGroups.update,
    successMessage: ({ variables }) => {
      const action = variables.enabled ? 'enabled' : 'disabled';
      const type = variables.enabled && variables.enforcement ? ` (${variables.enforcement})` : '';
      return `MFA ${action} for eligible admins${type}`;
    },
    errorMessage: ({ error }) => {
      return error?.response?.data?.error || 'Failed to update MFA configuration';
    },
    onSuccess: () => {
      setMfaConfigModalOpen(false);
    },
    onSettled: () => setBulkMfaLoading(false)
  });

  const [tab, setTab] = useState('1');
  const [globalMfaEnabled, setGlobalMfaEnabled] = useState(null);
  const [open, setOpen] = useState(false)
  const [successOpen, setSuccessOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [tabSortState, setTabSortState] = useState({
    '1': { sortBy: '', sortOrder: 'asc' },
    '2': { sortBy: '', sortOrder: 'asc' },
  });
  const [bulkMfaLoading, setBulkMfaLoading] = useState(false);
  const [globalMfaEnforcement, setGlobalMfaEnforcement] = useState('mandatory');
  const [openEdit, setOpenEdit] = useState(false);
  const [updateAdmin, setUpdateAdmin] = useState({})
  const [editSuccess, setEditSuccess] = useState(false)
  const [editRoles, setEditRoles] = useState({})
  const [search, setSearch] = useState('');
  const [searchTexts, setSearchTexts] = useState({});
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('');
  const [restaurantAdmin, setRestaurantAdmin] = useState({
    first_name: '',
    last_name: '',
    phone_no: '',
    email: '',
    username: '',
    top_level_access: [],
    instance_level_access: []
  })
  const [error, setError] = useState({
    first_name: '',
    last_name: '',
    phone_no: '',
    email: '',
    username: ''
  })
  const [editError, setEditError] = useState({
    first_name: '',
    last_name: '',
    phone_no: '',
    email: '',
    username: ''
  })
  
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [status, setStatus] = useState(false)
  const [fetchList, setFetchList] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const { ability } = useContext(AbilityContext)
  const createUser = strictCanScope(ability, 'create', 'Restaurant_Group');
  const readPermission = strictCanScope(ability, 'read', 'Restaurant_Group');
  const queryClient = useQueryClient();
  const showNotification = useNotificationContext();
  const [topLevelRole, setTopLevelRole] = useState([]);
  const [instanceRole, setInstanceRole] = useState([]);
  const [referenceTopLevelRole, setReferenceTopLevelRole] = useState([])
  const [updateTopLevelRole, setUpdateTopLevelRole] = useState([])
  const [referenceInstanceLevelRole, setReferenceInstanceLevelRole] = useState([])
  const [mfaConfigModalOpen, setMfaConfigModalOpen] = useState(false);
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [moduleKeys, setModuleKeys] = useState([])
  const [updateModuleKeys, setUpdateModuleKeys] = useState([])
  const [referenceModuleKeys, setReferenceModuleKeys] = useState([])
  const [profileId, setProfileId] = useState(NO_PROFILE_VALUE)
  const [updateProfileId, setUpdateProfileId] = useState(NO_PROFILE_VALUE)
  const [referenceProfileId, setReferenceProfileId] = useState(NO_PROFILE_VALUE)
  const [pendingProfileChange, setPendingProfileChange] = useState(null)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)

  React.useEffect(() => {
    const fetchGlobalMfaStatus = async () => {
      try {
        const response = await api.get('/v1/admin/admins/global_mfa_status');
        setGlobalMfaEnabled(response?.data?.global_mfa_enabled);
        setGlobalMfaEnforcement(response?.data?.global_mfa_enforcement || 'mandatory');
      } catch (err) {
        setGlobalMfaEnabled(null);
      }
    };
    fetchGlobalMfaStatus();
  }, []);

  useEffect(() => {
    if (!(editRoles?.length > 0)) {
      setUpdateTopLevelRole([])
      setReferenceTopLevelRole([])
      setInstanceRole([])
      setReferenceInstanceLevelRole([])
      return
    }

    const topLevelRoles = editRoles?.filter((data) => {
      if((data?.role === 'admin' || data?.role === 'viewer') && !(data?.instance)){
        return data
      }
    })
    let roleList = []
    topLevelRoles?.forEach((data) => {
      roleList.push(data?.role)
    })

    const instanceLevelRoles = editRoles?.filter((data) => {
      if(data?.instance){
        return data
      }
    })

    const instanceRolesList = formatInstanceRole(instanceLevelRoles)
    setInstanceRole(instanceRolesList)
    setReferenceInstanceLevelRole(instanceRolesList)
    setReferenceTopLevelRole(roleList)
    setUpdateTopLevelRole(roleList)
  }, [editRoles])

  const {
    groups: moduleGroups,
    moduleKeySet,
    selectableKeySet,
    isLoading: modulesLoading,
    isError: modulesError,
    refetch: refetchModules,
  } = usePermissionModules({ enabled: open || openEdit })

  const showCreateModuleAccess = topLevelRole.length === 0
  const showEditModuleAccess = updateTopLevelRole.length === 0

  // Restaurant Groups is reachable only through a Restaurant Group instance role. A user scoped to
  // individual restaurants would be granted the module and still find the screens closed to them,
  // so it is taken off the table until at least one group-level role is in the form. Keyed by module
  // so a second rule of this kind is one more entry, not more branching.
  // A chosen instance, not just a chosen type: a row still missing one is rejected on save
  // (`checkInstanceRoleData`), so it cannot become group access and must not unlock the module.
  const hasRestaurantGroupScope = useMemo(
    () =>
      instanceRole.some(
        (instance) => instance?.resourceType === 'Restaurant_Group' && Boolean(instance?.resourceInstance)
      ),
    [instanceRole]
  )

  const unavailableModules = useMemo(
    () =>
      hasRestaurantGroupScope
        ? EMPTY_UNAVAILABLE_MODULES
        : {
            restaurant_groups: {
              label: 'Needs group access',
              hint:
                'This user has no Restaurant Group instance access, so the Restaurant Groups screens ' +
                'would stay out of reach. Add a Restaurant Group instance role to grant it.',
            },
          },
    [hasRestaurantGroupScope]
  )

  // What may actually be ticked here: the catalogue's assignable keys less anything this user's own
  // scope rules out. Derived once and shared, so the grid, the counter and the payload filter can
  // never disagree about which keys are on offer.
  const grantableKeySet = useMemo(() => {
    const blockedKeys = Object.keys(unavailableModules)

    if (blockedKeys.length === 0) return selectableKeySet

    const grantable = new Set(selectableKeySet)
    blockedKeys.forEach((key) => grantable.delete(key))

    return grantable
  }, [selectableKeySet, unavailableModules])

  const moduleCatalogueReady =
    !modulesLoading && !modulesError && moduleGroups.length > 0 && grantableKeySet.size > 0

  const usableModuleKeys = useCallback(
    (keys) => (Array.isArray(keys) ? keys.filter((key) => grantableKeySet.has(key)) : []),
    [grantableKeySet]
  )

  const sameModuleSelection = useCallback((left, right) => {
    if (left.length !== right.length) return false
    const rightSet = new Set(right)
    return left.every((key) => rightSet.has(key))
  }, [])

  // Pre-populates the tick-boxes from the raw grant. `accessible_module_keys`, not
  // `accessible_modules`: the latter reads "everything" for a network-wide user, so clearing
  // their top-level role in this form would reveal a fully ticked grid they never had.
  useEffect(() => {
    const storedKeys = Array.isArray(updateAdmin?.accessible_module_keys)
      ? updateAdmin.accessible_module_keys
      : []
    const storedProfileId = updateAdmin?.application_permission_profile?.id ?? NO_PROFILE_VALUE

    setUpdateModuleKeys(storedKeys)
    setReferenceModuleKeys(storedKeys)
    setUpdateProfileId(storedProfileId)
    setReferenceProfileId(storedProfileId)
    setPendingProfileChange(null)
  }, [updateAdmin?.id])

  const {
    profiles,
    profilesById,
    isLoading: profilesLoading,
    isError: profilesError,
    refetch: refetchProfiles,
  } = usePermissionProfiles({ enabled: open || openEdit })

  const selectedInstanceRoles = useMemo(
    () => Array.from(new Set(instanceRole.map((instance) => instance?.resourceRole).filter(Boolean))),
    [instanceRole]
  )

  const profileOptions = useMemo(
    () => filterProfilesForRoles(profiles, selectedInstanceRoles),
    [profiles, selectedInstanceRoles]
  )

  const moduleLabels = useMemo(() => {
    const labels = {}
    moduleGroups.forEach((group) => {
      (group?.modules || []).forEach((module) => {
        if (module?.key) labels[module.key] = module.label || module.key
      })
    })
    return labels
  }, [moduleGroups])

  const createProfile = useMemo(
    () => (profileId ? profilesById.get(profileId) || null : null),
    [profileId, profilesById]
  )

  const assignedEditProfile = useMemo(() => {
    const stored = updateAdmin?.application_permission_profile
    if (!stored?.id) return null

    return profilesById.get(stored.id) || { ...stored, module_keys: null }
  }, [updateAdmin?.application_permission_profile, profilesById])

  const editProfile = useMemo(() => {
    if (!updateProfileId) return null

    return (
      profilesById.get(updateProfileId) ||
      (assignedEditProfile?.id === updateProfileId ? assignedEditProfile : null)
    )
  }, [updateProfileId, profilesById, assignedEditProfile])

  const profileOptionIds = useMemo(
    () => new Set(profileOptions.map((profile) => profile.id)),
    [profileOptions]
  )

  const profileNotApplicable = useCallback(
    (id) => Boolean(id) && profilesById.has(id) && !profileOptionIds.has(id),
    [profilesById, profileOptionIds]
  )

  const createSummary = useMemo(
    () => buildModuleAccessSummary({ profile: createProfile, selectedKeys: moduleKeys, grantableKeySet }),
    [createProfile, moduleKeys, grantableKeySet]
  )

  const editSummary = useMemo(
    () =>
      buildModuleAccessSummary({
        profile: editProfile,
        selectedKeys: updateModuleKeys,
        grantableKeySet,
        fallbackCustomized: Boolean(updateAdmin?.module_access_customized),
      }),
    [editProfile, updateModuleKeys, grantableKeySet, updateAdmin?.module_access_customized]
  )

  const applyProfileChange = useCallback((mode, nextProfileId, nextKeys) => {
    if (mode === 'create') {
      setProfileId(nextProfileId)
      if (Array.isArray(nextKeys)) setModuleKeys(nextKeys)
      return
    }

    setUpdateProfileId(nextProfileId)
    if (Array.isArray(nextKeys)) setUpdateModuleKeys(nextKeys)
  }, [])

  const resolveProfile = useCallback((mode, id) => {
    if (!id) return null

    return (
      profilesById.get(id) ||
      (mode === 'edit' && assignedEditProfile?.id === id ? assignedEditProfile : null)
    )
  }, [profilesById, assignedEditProfile])

  const requestProfileChange = useCallback((mode, nextProfileId) => {
    const nextProfile = resolveProfile(mode, nextProfileId)

    if (!nextProfile) {
      applyProfileChange(mode, NO_PROFILE_VALUE, null)
      return
    }

    if (!Array.isArray(nextProfile.module_keys)) {
      applyProfileChange(mode, nextProfileId, null)
      return
    }

    const nextKeys = usableModuleKeys(nextProfile.module_keys)
    const nextKeySet = new Set(nextKeys)
    const currentKeys = usableModuleKeys(mode === 'create' ? moduleKeys : updateModuleKeys)
    const losingKeys = currentKeys.filter((key) => !nextKeySet.has(key))

    if (losingKeys.length === 0) {
      applyProfileChange(mode, nextProfileId, nextKeys)
      return
    }

    setPendingProfileChange({
      mode,
      profileId: nextProfileId,
      profileName: nextProfile.name,
      keys: nextKeys,
      losingLabels: losingKeys.map((key) => moduleLabels[key] || key),
    })
  }, [resolveProfile, usableModuleKeys, moduleKeys, updateModuleKeys, moduleLabels, applyProfileChange])

  const confirmProfileChange = useCallback(() => {
    if (!pendingProfileChange) return

    applyProfileChange(pendingProfileChange.mode, pendingProfileChange.profileId, pendingProfileChange.keys)
    setPendingProfileChange(null)
  }, [pendingProfileChange, applyProfileChange])

  const normalizedSearchQuery = useMemo(() => normalizeSearchTerm(search), [search]);
  const debouncedSearchQuery = useDebounce(normalizedSearchQuery, 300);
  const hasActiveSearch = debouncedSearchQuery.length > 0;
  const isSearchLoading = normalizedSearchQuery.length > 0 && normalizedSearchQuery !== debouncedSearchQuery;

  const fetchAdmins = useCallback(async () => {
    const endpoint = tab === '2' ? '/v1/admin/admins/quarantined' : '/v1/admin/admins?role=restaurant_admin';
    const currentSort = tabSortState[tab] || { sortBy: '', sortOrder: 'asc' };
    const response = await api.get(endpoint, {
      params: {
        per_page: itemsPerPage,
        page: page,
        ...(debouncedSearchQuery ? { search: debouncedSearchQuery } : {}),
        ...(currentSort.sortBy ? { sort_by: currentSort.sortBy, sort_order: currentSort.sortOrder } : {}),
      }
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }, [tab, itemsPerPage, page, tabSortState, debouncedSearchQuery])

  const handleSortChange = useCallback((nextSortBy, nextSortOrder) => {
    setTabSortState((prev) => ({
      ...prev,
      [tab]: {
        sortBy: nextSortBy,
        sortOrder: nextSortOrder,
      },
    }));
    setPage(1);
  }, [tab]);

  const { data: adminUsers, isFetching } = useQuery({
    queryKey: Query_Keys.admins({
      page: page,
      itemsPerPage: itemsPerPage,
      tab: tab,
      search: debouncedSearchQuery,
      sortBy: tabSortState[tab]?.sortBy,
      sortOrder: tabSortState[tab]?.sortOrder,
    }),
    queryFn: fetchAdmins,
    staleTime: 10 * 60 * 1000,
  })

  const usersForTable = adminUsers?.data || [];
  const usersPageCount = Number(adminUsers?.pageCount || 0);
  const isInitialUsersLoad = !adminUsers && isFetching && normalizedSearchQuery.length === 0;
  const disableCsvDownloadButton = usersForTable.length === 0 || csvLoading;

  const handleSearch = useCallback((searchQuery) => {
    setSearch(searchQuery);
    setPage(1);
  }, []);

  const fetchRestaurantGroups = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })

    return response?.data?.map((group) => {
      const normalizedGroup = normalizeRestaurantGroup(group)

      return {
        id: normalizedGroup?.label,
        key: normalizedGroup?.label,
        value: normalizedGroup?.value,
      }
    }) || []
  }, [debouncedRestaurantGroupSearch])

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: 'user_management_instance',
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    enabled: fetchList,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const fetchRestaurants = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      },
    })

    return response?.data?.map((restaurant) => {
      const normalizedRestaurant = normalizeRestaurant(restaurant)

      return {
        id: normalizedRestaurant?.id,
        key: normalizedRestaurant?.id,
        value: normalizedRestaurant?.name,
      }
    }) || []
  }, [debouncedRestaurantSearch])

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: null,
      search: debouncedRestaurantSearch,
      source: 'user_management_instance',
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    enabled: fetchList,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const handleCreateOpen = useCallback(() => {
    setOpen(true)
    setFetchList(true)
  }, [])

  const handleCreateClose = useCallback(() => {
    setOpen(false)
    setFetchList(false)
    setRestaurantSearch('')
    setRestaurantGroupSearch('')
    setRestaurantAdmin({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: '',
      top_level_access: [],
      instance_level_access: [],
    })
    setError({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: ''
    })
    setTopLevelRole([])
    setInstanceRole([])
    setModuleKeys([])
    setProfileId(NO_PROFILE_VALUE)
    setPendingProfileChange(null)
  }, [])

  const change = (value, field) => {
    setRestaurantAdmin({...restaurantAdmin, [field]: value})
    if(error[field] !== '') setError({...error, [field]: ''})
  }

  const update = (value, field) => {
    setUpdateAdmin({...updateAdmin, [field]: value})
    if(editError[field] !== '') setEditError({...editError, [field]: ''})
  }

  const ERROR_MESSAGES = {
    unauthorized: "You don't have permission to perform this action",
    mailer: 'Admin is created but failed to send an email'
  }

  const getErrorMessage = (errors = {}) => {
    const key = Object.keys(errors)?.[0]
    return Object.hasOwn(ERROR_MESSAGES, key) ? { errorMessage: ERROR_MESSAGES[key], errorKey: key } : ''
  }

  const handleAdd = useCallback(async () => {
    setStatus(true)
    const payload = {
      ...restaurantAdmin,
      phone_no: restaurantAdmin?.phone_no.length <= 4 ? '' : restaurantAdmin?.phone_no,
    }

    if (moduleCatalogueReady && showCreateModuleAccess) {
      payload.module_keys = usableModuleKeys(moduleKeys)

      // Sent alongside the keys, never instead of them: the grid is the authority on what this
      // user gets, and the id only records which profile it started from.
      if (profileId) payload.application_permission_profile_id = profileId
    }

    if(instanceRole?.length > 0) {
      const validData = checkInstanceRoleData()
      if(validData?.isValid){
        payload.instance_level_access = transformInstanceRoleData()
      }
      else {
        showNotification('Instance level roles cannot be blank', 'error')
        setStatus(false)
        return
      }
    }
    try {
      await createAdminMutation(payload);
    } catch (error) {
      const errors = error?.response?.data?.errors
      const apiError = getErrorMessage(errors)

      if(!apiError) {
        setError(errors)
        setStatus(false)
        return
      }

      if(apiError?.errorKey === 'mailer') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['admins'] }),
          queryClient.invalidateQueries({ queryKey: ['admin_user_dropdown'] })
        ])
        showNotification(`User '${restaurantAdmin?.first_name}' has been created successfully but failed to send an email`, 'info')
        setOpen(false)
        setStatus(false)
        return
      }

      showNotification(apiError?.errorMessage, 'error')
      setStatus(false)
    }
  }, [restaurantAdmin, instanceRole, moduleKeys, profileId, moduleCatalogueReady, showCreateModuleAccess, usableModuleKeys])

  const handleUpdate = useCallback(async () => {
    setStatus(true)
    const originalRoles = referenceTopLevelRole || [];
    const updatedRoles = updateTopLevelRole || [];

    const originalInstanceRoles = referenceInstanceLevelRole || [];
    const updatedInstanceRoles = instanceRole || [];

    const addedRoles = updatedRoles.filter(role => !originalRoles.includes(role));
    const removedRoles = originalRoles.filter(role => !updatedRoles.includes(role));

    const addedInstanceRoles = handleUpdateInstanceRole(originalInstanceRoles, updatedInstanceRoles)
    const removedInstanceRoles = handleRemoveInstanceRole(originalInstanceRoles, updatedInstanceRoles)
    if(addedInstanceRoles?.length > 0){
      const validData = checkInstanceRoleData()
      if(validData?.isValid === false) {
        setStatus(false)
        showNotification('Instance level roles cannot be blank', 'error')
        return
      }
    }

    // Sent only when the admin actually changed the selection or the profile. Omitting an
    // untouched list is what preserves module access through an edit that only changes, say, a
    // phone number — and through a promotion, where the stored set has to survive to reappear on a
    // later demotion.
    const nextModuleKeys = usableModuleKeys(updateModuleKeys)
    const moduleSelectionChanged =
      !sameModuleSelection(nextModuleKeys, usableModuleKeys(referenceModuleKeys))
    const profileChanged = (updateProfileId || null) !== (referenceProfileId || null)
    const includeModuleAccess =
      moduleCatalogueReady && showEditModuleAccess && (moduleSelectionChanged || profileChanged)

    const updatePayload = {
      ...updateAdmin,
      ...(addedRoles.length > 0 && { top_level_access: addedRoles }),
      ...(removedRoles.length > 0 && { delete_top_level: removedRoles }),
      ...(addedInstanceRoles?.length > 0 && {instance_level_access: addedInstanceRoles}),
      ...(removedInstanceRoles?.length > 0 && {delete_instance_level: removedInstanceRoles}),
      // Both go together whenever either moved: the keys the grid shows are the ones that should
      // be stored, so the server is never left to re-derive them from the profile.
      ...(includeModuleAccess && {
        module_keys: nextModuleKeys,
        application_permission_profile_id: updateProfileId || null,
      })
    };

    // `application_permission_profile` is the read-only object the detail endpoint returns; the
    // request carries the id instead, so spreading `updateAdmin` must not put it back on the wire.
    delete updatePayload.application_permission_profile
    delete updatePayload.module_access_customized

    if(updateAdmin?.phone_no.length <= 4){
      updateAdmin.phone_no = ''
    }

    try {
      await updateAdminMutation({ id: updateAdmin?.id, payload: updatePayload })
    } catch (error) {
      const errors = error?.response?.data?.errors || {}
      const errorKeys = Object.keys(errors)
      if(errorKeys[0] === 'unauthorized'){
        setOpenEdit(false)
        setErrorOpen(true)
      }
      else if(errorKeys.includes('role')){
        const roleErrors = errors.role
        const errorMessage = Array.isArray(roleErrors) ? roleErrors[0] : roleErrors
        showNotification(errorMessage, 'error')
      }
      else{
        setEditError(errors)
      }
      setStatus(false)
    }
  }, [updateAdmin, referenceTopLevelRole, updateTopLevelRole, referenceInstanceLevelRole, instanceRole,
      updateModuleKeys, referenceModuleKeys, updateProfileId, referenceProfileId, moduleCatalogueReady,
      showEditModuleAccess, usableModuleKeys, sameModuleSelection])

  const handleClose = useCallback(() => {
    setSuccessOpen(false)
    setRestaurantAdmin({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: '',
      top_level_access: [],
      instance_level_access: [],
    })
    setTopLevelRole([])
    setInstanceRole([])
    setModuleKeys([])
    setProfileId(NO_PROFILE_VALUE)
    setPendingProfileChange(null)
  }, [])

  const handleEditClose = useCallback(() => {
    setOpenEdit(false)
    setRestaurantSearch('')
    setRestaurantGroupSearch('')
    setEditError({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: ''
    })
    setUpdateAdmin({})
    setUpdateTopLevelRole([])
    setInstanceRole([])
    setReferenceInstanceLevelRole([])
    setUpdateModuleKeys([])
    setReferenceModuleKeys([])
    setUpdateProfileId(NO_PROFILE_VALUE)
    setReferenceProfileId(NO_PROFILE_VALUE)
    setPendingProfileChange(null)
  }, [])

  const handleTopLevelChange = (event) => {
    const {
      target: { value },
    } = event;
    setTopLevelRole(
      typeof value === 'string' ? value.split(',') : value,
    );
    setRestaurantAdmin({...restaurantAdmin, top_level_access: typeof value === 'string' ? value.split(',') : value})
  };

  const handleTopLevelDelete = (value) => {
    const topLevelRoles = topLevelRole.filter((role) => role !== value)
    setTopLevelRole(topLevelRoles)
    setRestaurantAdmin({...restaurantAdmin, top_level_access: topLevelRoles})
  }

  const addInstanceRole = () => {
    id++
    setInstanceRole([...instanceRole, {id: id, resourceType: '', resourceInstance: '', resourceRole: ''}])
  }

  const removeInstanceRole = (id) => {
    const instanceRoles = instanceRole.filter((instance) => instance?.id !== id)
    setInstanceRole(instanceRoles)
  }

  const handleResourceType = (id, resource) => {
    let instanceResource = instanceRole?.map((instance) => {
      if(instance?.id === id) {
        return {...instance, resourceType: resource, resourceRole: ''}
      }
      return instance
    })
    setInstanceRole(instanceResource)
  }

  const handleResourceRole = (id, role) => {
    let instanceRoles = instanceRole?.map((instance) => {
      if(instance?.id === id) {
        return {...instance, resourceRole: role}
      }
      return instance
    })
    setInstanceRole(instanceRoles)
  }

  const handleResourceInstance = (id, instance) => {
    let instanceKey = instanceRole?.map((key) => {
      if(key?.id === id) {
        return { ...key, resourceInstance: instance }
      }
      return key
    })
    setInstanceRole(instanceKey)
  }

  const checkInstanceRoleData = () => {
    for(const obj of instanceRole){
      for(const key in obj){
        if (obj[key] === '') {
          return { isValid: false, invalidId: obj.id };
        }
      }
    }
    return { isValid: true };
  }

  const transformInstanceRoleData = () => {
    const transformed = instanceRole.map( obj => ({
      resource_instance: `${obj.resourceType}:${obj.resourceInstance}`,
      role: `${obj.resourceRole}`,
    }))
    return transformed
  }

  // Edit User Functions
  const handleEditTopLevelChange = (event) => {
     const {
      target: { value },
    } = event;
    setUpdateTopLevelRole(
      typeof value === 'string' ? value.split(',') : value,
    );
  }

  const handleEditTopLevelDelete = (value) => {
    const topLevelRoles = updateTopLevelRole.filter((role) => role !== value)
    setUpdateTopLevelRole(topLevelRoles)
  }

  const formatInstanceRole = (role) => {
    let id = 0
    let formattedInstanceRoles = []
    role?.forEach((data) => {
      id++
      let instance = data?.instance.split(':')
      formattedInstanceRoles.push(
        {
          id: id,
          resourceType: instance?.[0],
          resourceInstance: instance?.[1],
          resourceRole: data?.role,
        }
      )
    })
    return formattedInstanceRoles
  }

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantGroups, 'id', ['value']))
  }, [restaurantGroups])

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurants, 'id', ['value']))
  }, [restaurants])

  useEffect(() => {
    const instanceLevelRoles = Array.isArray(editRoles) ? editRoles.filter((role) => role?.instance) : []
    const instanceLevelDisplayRoles = updateAdmin?.roles?.instance_level

    if (instanceLevelRoles.length === 0 || !Array.isArray(instanceLevelDisplayRoles)) {
      return
    }

    const nextRestaurantGroupOptions = []
    const nextRestaurantOptions = []

    instanceLevelRoles.forEach((roleData, index) => {
      const [resourceType, instanceId] = `${roleData?.instance || ''}`.split(':', 2)
      const displayRole = instanceLevelDisplayRoles[index]

      if (!resourceType || !instanceId || !displayRole) {
        return
      }

      const separatorIndex = displayRole.lastIndexOf('#')
      const resourceLabel = separatorIndex >= 0 ? displayRole.slice(0, separatorIndex) : displayRole
      const resourcePrefix = `${resourceType}:`
      const displayName = resourceLabel.startsWith(resourcePrefix)
        ? resourceLabel.slice(resourcePrefix.length)
        : ''

      if (!displayName) {
        return
      }

      const option = {
        id: instanceId,
        key: instanceId,
        value: displayName,
      }

      if (resourceType === 'Restaurant_Group') {
        nextRestaurantGroupOptions.push(option)
      }

      if (resourceType === 'Restaurant') {
        nextRestaurantOptions.push(option)
      }
    })

    if (nextRestaurantGroupOptions.length > 0) {
      setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, nextRestaurantGroupOptions, 'id'))
    }

    if (nextRestaurantOptions.length > 0) {
      setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, nextRestaurantOptions, 'id'))
    }
  }, [editRoles, updateAdmin?.roles?.instance_level])

  const missingRestaurantGroupIds = useMemo(() => [...new Set(
    instanceRole
      .filter((instance) => instance?.resourceType === 'Restaurant_Group' && instance?.resourceInstance)
      .map((instance) => `${instance.resourceInstance}`)
      .filter((instanceId) => !restaurantGroupOptionsCache?.some((option) => `${option?.id}` === instanceId))
  )], [instanceRole, restaurantGroupOptionsCache])

  const missingRestaurantIds = useMemo(() => [...new Set(
    instanceRole
      .filter((instance) => instance?.resourceType === 'Restaurant' && instance?.resourceInstance)
      .map((instance) => `${instance.resourceInstance}`)
      .filter((instanceId) => !restaurantOptionsCache?.some((option) => `${option?.id}` === instanceId))
  )], [instanceRole, restaurantOptionsCache])

  const missingRestaurantGroupIdsSignature = missingRestaurantGroupIds.join(',')
  const missingRestaurantIdsSignature = missingRestaurantIds.join(',')

  useEffect(() => {
    if (!openEdit || instanceRole.length === 0) {
      return
    }

    if (missingRestaurantGroupIds.length === 0 && missingRestaurantIds.length === 0) {
      return
    }

    let cancelled = false

    const hydrateMissingInstanceLabels = async () => {
      const [restaurantGroupResults, restaurantResults] = await Promise.all([
        Promise.all(missingRestaurantGroupIds.map(async (instanceId) => {
          try {
            const response = await api.get(`/v1/admin/restaurant_groups/${instanceId}`)
            return {
              id: `${response?.data?.id ?? instanceId}`,
              key: `${response?.data?.id ?? instanceId}`,
              value: response?.data?.name,
            }
          } catch {
            return null
          }
        })),
        Promise.all(missingRestaurantIds.map(async (instanceId) => {
          try {
            const response = await api.get(`/v1/admin/restaurants/${instanceId}`)
            return {
              id: `${response?.data?.id ?? instanceId}`,
              key: `${response?.data?.id ?? instanceId}`,
              value: response?.data?.name,
            }
          } catch {
            return null
          }
        })),
      ])

      if (cancelled) {
        return
      }

      const validRestaurantGroups = restaurantGroupResults.filter((option) => option?.value)
      const validRestaurants = restaurantResults.filter((option) => option?.value)

      if (validRestaurantGroups.length > 0) {
        setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, validRestaurantGroups, 'id'))
      }

      if (validRestaurants.length > 0) {
        setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, validRestaurants, 'id'))
      }
    }

    hydrateMissingInstanceLabels()

    return () => {
      cancelled = true
    }
  }, [missingRestaurantGroupIdsSignature, missingRestaurantIdsSignature, openEdit])

  const selectedRestaurantGroupOptions = restaurantGroupOptionsCache?.filter((restaurantGroup) => (
    instanceRole?.some((instance) => instance?.resourceType === 'Restaurant_Group' && `${instance?.resourceInstance}` === `${restaurantGroup?.id}`)
  ))

  const displayedRestaurantGroups = mergeUniqueByKey(restaurantGroups, selectedRestaurantGroupOptions, 'id')

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) => (
    instanceRole?.some((instance) => instance?.resourceType === 'Restaurant' && `${instance?.resourceInstance}` === `${restaurant?.id}`)
  ))

  const displayedRestaurants = mergeUniqueByKey(restaurants, selectedRestaurantOptions, 'id')

  const getList = useCallback((resource) => {
    switch(resource) {
      case 'Restaurant_Group': {
        return restaurantGroupSearch.trim() ? restaurantGroups : displayedRestaurantGroups
      }
      case 'Restaurant': {
        return restaurantSearch.trim() ? restaurants : displayedRestaurants
      }
      default: {
        return []
      }
    }
  }, [displayedRestaurantGroups, displayedRestaurants, restaurantGroupSearch, restaurantGroups, restaurantSearch, restaurants])

  const handleUpdateInstanceRole = (originalInstanceRoles, addedInstanceRoles) => {
    const formattedRoles = transformInstance(originalInstanceRoles)
    const formattedAddedRoles = transformInstance(addedInstanceRoles)
    const newAddedInstanceRoles = formattedAddedRoles?.filter((role) => !formattedRoles.includes(role))
    let tranformInstanceRole = []

    newAddedInstanceRoles?.forEach((role) => {
      const instance = (role?.split('#'))
      tranformInstanceRole.push({ resource_instance: instance?.[0], role: instance?.[1] })
    })

    return tranformInstanceRole
  }

  const handleRemoveInstanceRole = (originalInstanceRoles, removedInstanceRoles) => {
    const formattedRoles = transformInstance(originalInstanceRoles)
    const formattedRemovedRoles = transformInstance(removedInstanceRoles)
    const removedRoles = formattedRoles?.filter((role) => !formattedRemovedRoles.includes(role))
    let tranformInstanceRole = []

    removedRoles?.forEach((role) => {
      const instance = (role?.split('#'))
      tranformInstanceRole.push({ resource_instance: instance?.[0], role: instance?.[1] })
    })

    return tranformInstanceRole
  }

  const handleAdminCsvExport = async () => {
    try {
      setCsvLoading(true);
      const panel =  tab === '2' ? 'quarantined_admins' : 'admins'
      const searchQuery = debouncedSearchQuery || normalizedSearchQuery;
      const query = buildQueryParams({ ...(searchQuery ? { search: searchQuery } : {}) });
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=${panel}${query ? `&${query}` : ''}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      const searchSuffix = searchQuery
        ? `_search-${searchQuery.replace(/\s+/g, '-').slice(0, 40)}`
        : '';
      let filename = `${panel}${searchSuffix}_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const transformInstance = useCallback((data) => {
    let roles = []
    data?.forEach((role) => {
      roles.push(`${role?.resourceType}:${role?.resourceInstance}#${role?.resourceRole}`)
    })
    return roles
  }, [])

  const handleTabChange = useCallback((event, newValue) => {
    setTab(newValue);
    setPage(1);
    setItemsPerPage(25);
  }, [])

  const getInstanceName = useCallback((resourceType, instanceId) => {
    if(resourceType === 'Restaurant_Group') {
      const restaurantGroup = restaurantGroupOptionsCache?.find((res) => `${res?.id}` === `${instanceId}`)
      return restaurantGroup ? restaurantGroup?.value : ''
    }
    if(resourceType === 'Restaurant') {
      const restaurant = restaurantOptionsCache?.find((res) => `${res?.id}` === `${instanceId}`)
      return restaurant ? restaurant?.value : ''
    }
  }, [restaurantGroupOptionsCache, restaurantOptionsCache])

  const handleOpenMfaConfig = () => {
    setMfaConfigModalOpen(true);
  };

  const handleCloseMfaConfig = () => {
    setGlobalMfaEnabled(globalMfaEnabled);
    setGlobalMfaEnforcement(globalMfaEnforcement);
    setMfaConfigModalOpen(false);
  };

  const handleSaveMfaConfig = async (enabled, enforcement = null) => {
    setBulkMfaLoading(true);
    try {
      await updateBulkMfaMutation({ enabled: enabled, enforcement: enabled && enforcement ? enforcement : undefined });
      setGlobalMfaEnabled(enabled);
      if (enabled) {
        setGlobalMfaEnforcement(enforcement);
      }
    } catch (err) {
      // Error handled in mutation onError
    }
  };

  const isEditLoading = !updateAdmin?.id

  const instanceHandlers = {
    onAdd: addInstanceRole,
    onRemove: removeInstanceRole,
    onResourceTypeChange: handleResourceType,
    onResourceInstanceChange: handleResourceInstance,
    onResourceRoleChange: handleResourceRole,
  }

  const instanceResources = {
    Restaurant_Group: {
      menuRef: restaurantGroupMenuRef,
      loaderRef: restaurantGroupLoaderRef,
      hasNextPage: hasNextRestaurantGroupsPage,
      isFetching: restaurantGroupFetching,
      onSearchChange: setRestaurantGroupSearch,
    },
    Restaurant: {
      menuRef: restaurantMenuRef,
      loaderRef: restaurantLoaderRef,
      hasNextPage: hasNextRestaurantsPage,
      isFetching: restaurantFetching,
      onSearchChange: setRestaurantSearch,
    },
  }

  const moduleAccessCatalogue = {
    groups: moduleGroups,
    moduleKeySet,
    grantableKeySet,
    unavailableModules,
    modulesLoading,
    modulesError,
    refetchModules,
    catalogueReady: moduleCatalogueReady,
    profileOptions,
    profilesLoading,
    profilesError,
    refetchProfiles,
    hasRoles: selectedInstanceRoles.length > 0,
  }

  const createModuleAccess = showCreateModuleAccess ? {
    ...moduleAccessCatalogue,
    selectedKeys: moduleKeys,
    onSelectedKeysChange: setModuleKeys,
    profileId,
    selectedProfile: createProfile,
    assignedProfile: null,
    isProfileNotApplicable: profileNotApplicable(profileId),
    onProfileChange: (nextId) => requestProfileChange('create', nextId),
    summaryLabel: createSummary.label,
    profileKeySet: createSummary.profileKeySet,
  } : null

  const editModuleAccess = showEditModuleAccess ? {
    ...moduleAccessCatalogue,
    selectedKeys: updateModuleKeys,
    onSelectedKeysChange: setUpdateModuleKeys,
    profileId: updateProfileId,
    selectedProfile: editProfile,
    assignedProfile: assignedEditProfile,
    isProfileNotApplicable: profileNotApplicable(updateProfileId),
    onProfileChange: (nextId) => requestProfileChange('edit', nextId),
    summaryLabel: editSummary.label,
    profileKeySet: editSummary.profileKeySet,
  } : null

  return(
    <RestaurantAdminContainer>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink 
            LinkComponent={StyledLink} 
            label="Home" to="/admin/dashboard" 
          />
          <StyledLink color={'#515152'}>
            User Management
          </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      {/* Success Alert */}
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          variant="filled"
          sx={{width: '100%'}}
        >
          {`User '${restaurantAdmin?.first_name}' has been created successfully`}
        </Alert>
      </Snackbar>

      <Snackbar
        open={editSuccess}
        autoHideDuration={4000}
        onClose={() => setEditSuccess(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setEditSuccess(false)}
          severity="success"
          variant="filled"
          sx={{width: '100%'}}
        >
          {`User '${updateAdmin?.first_name}' has been updated successfully`}
        </Alert>
      </Snackbar>

      {/* Error Alert */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          variant="filled"
          sx={{width: '100%'}}
        >
          {"You don't have permissions to create or edit user"}
        </Alert>
      </Snackbar>

      <StyledTitleContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <StyledTitle>Users</StyledTitle>

          <StyledSearchBar
            value={search}
            placeholder="Search users"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
            inputProps={{ 'data-testid': 'users-search-input' }}
          />
        </div>
      </StyledTitleContainer>

      <Divider sx={{ borderStyle: 'solid', borderWidth: 1 }} />

      <Box sx={{ padding: '25px 0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <StyledCreateButton
          onClick={handleCreateOpen}
          variant="contained"
          startIcon={<AddIcon/>}
          disabled={!(superAdmin?.role === 'admin') || tab === '2'}
        >
          Create User
        </StyledCreateButton>

        <div style={{ display: 'flex', gap: 8 }}>
          <StyledCreateButton
            onClick={handleOpenMfaConfig}
            variant="contained"
            disabled={!(superAdmin?.role === 'admin') || tab === '2'}
          >
            {globalMfaEnabled ? 'Manage MFA' : 'Enable MFA'}
          </StyledCreateButton>
        </div>
      </Box>

      {/* MFA Configuration Modal */}
      <MfaConfigurationModal
        open={mfaConfigModalOpen}
        onClose={handleCloseMfaConfig}
        onSave={handleSaveMfaConfig}
        currentStatus={globalMfaEnabled}
        currentEnforcement={globalMfaEnforcement}
        loading={bulkMfaLoading}
      />

      {/* Tabs */}
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
          <TabList onChange={handleTabChange} aria-label="user management tabs">
            <StyledTab label="Active Users" value="1" sx={{textTransform: 'none'}}/>
            <StyledTab label="Quarantined Users" value="2" />
          </TabList>
        </Box>
        <StyledTabPanel value="1">
          {
            isInitialUsersLoad ? (
              <Loader />
            ) : (
              !readPermission ? (
                <div></div>
              ) : (
                <React.Fragment>
                  <RestaurantAdminsTable
                    data={usersForTable}
                    fetchAdmins={fetchAdmins}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    page={page}
                    setPage={setPage}
                    pageCount={usersPageCount}
                    fetchingLoader={isInitialUsersLoad}
                    searchLoading={isSearchLoading || (isFetching && hasActiveSearch)}
                    hasSearchTerm={normalizedSearchQuery.length > 0}
                    setEdit={setOpenEdit}
                    setUpdateAdmin={setUpdateAdmin}
                    setEditError={setEditError}
                    setEditRoles={setEditRoles}
                    setFetch={setFetchList}
                    tab={tab}
                    showDownloadButton={true}
                    disableDownloadButton={disableCsvDownloadButton}
                    handleExportCsv={handleAdminCsvExport}
                    csvLoading={csvLoading}
                    showNotification={showNotification}
                    sortBy={tabSortState[tab]?.sortBy}
                    sortOrder={tabSortState[tab]?.sortOrder}
                    onSortChange={handleSortChange}
                  />
                </React.Fragment>
              )
            )
          }
        </StyledTabPanel>
        <StyledTabPanel value="2">
          {
            isInitialUsersLoad ? (
              <Loader />
            ) : (
              !readPermission ? (
                <div></div>
              ) : (
                <React.Fragment>
                  <RestaurantAdminsTable
                    data={usersForTable}
                    fetchAdmins={fetchAdmins}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    page={page}
                    setPage={setPage}
                    pageCount={usersPageCount}
                    fetchingLoader={isInitialUsersLoad}
                    searchLoading={isSearchLoading || (isFetching && hasActiveSearch)}
                    hasSearchTerm={normalizedSearchQuery.length > 0}
                    setEdit={setOpenEdit}
                    setUpdateAdmin={setUpdateAdmin}
                    setEditError={setEditError}
                    setEditRoles={setEditRoles}
                    setFetch={setFetchList}
                    tab={tab}
                    showDownloadButton={true}
                    disableDownloadButton={disableCsvDownloadButton}
                    handleExportCsv={handleAdminCsvExport}
                    csvLoading={csvLoading}
                    showNotification={showNotification}
                    sortBy={tabSortState[tab]?.sortBy}
                    sortOrder={tabSortState[tab]?.sortOrder}
                    onSortChange={handleSortChange}
                  />
                </React.Fragment>
              )
            )
          }
        </StyledTabPanel>
      </TabContext>

      {/* Both are mounted only while open, the way the other create/edit modals in the app are.
          A permanently mounted Dialog leaves the `aria-hidden` MUI puts on the page behind it in
          place after closing, and the form would keep its last state between opens. */}
      {open && (
        <RestaurantAdminModal
          mode="create"
          open={open}
          onClose={handleCreateClose}
          onSubmit={handleAdd}
          isSubmitting={status}
          values={restaurantAdmin}
          errors={error}
          onFieldChange={change}
          topLevelRoles={topLevelRole}
          onTopLevelRolesChange={handleTopLevelChange}
          onTopLevelRoleDelete={handleTopLevelDelete}
          instanceRoles={instanceRole}
          instanceHandlers={instanceHandlers}
          resources={instanceResources}
          getList={getList}
          getInstanceName={getInstanceName}
          moduleAccess={createModuleAccess}
        />
      )}

      {openEdit && (
        <RestaurantAdminModal
          mode="edit"
          open={openEdit}
          isLoading={isEditLoading}
          onClose={handleEditClose}
          onSubmit={handleUpdate}
          isSubmitting={status}
          values={updateAdmin}
          errors={editError}
          onFieldChange={update}
          topLevelRoles={updateTopLevelRole}
          onTopLevelRolesChange={handleEditTopLevelChange}
          onTopLevelRoleDelete={handleEditTopLevelDelete}
          instanceRoles={instanceRole}
          instanceHandlers={instanceHandlers}
          resources={instanceResources}
          getList={getList}
          getInstanceName={getInstanceName}
          moduleAccess={editModuleAccess}
        />
      )}

      <ConfirmationDialog
        open={Boolean(pendingProfileChange)}
        onClose={() => setPendingProfileChange(null)}
        Icon={ErrorOutlineIcon}
        dialogTitle="This will replace the modules already ticked"
        dialogBody={
          <Box component="span" data-testid='profile-replace-dialog-body'>
            <Box component="span" sx={{ display: 'block', marginBottom: '6px' }}>
              {`'${pendingProfileChange?.profileName}' does not include ${
                pendingProfileChange?.losingLabels?.length === 1 ? 'this module' : 'these modules'
              }, so ${pendingProfileChange?.losingLabels?.length === 1 ? 'it' : 'they'} will be unticked:`}
            </Box>
            <Box component="span" sx={{ display: 'block', fontWeight: '600' }}>
              {pendingProfileChange?.losingLabels?.join(', ')}
            </Box>
            <Box component="span" sx={{ display: 'block', marginTop: '6px' }}>
              You can tick them back on afterwards.
            </Box>
          </Box>
        }
        confirmationText="Yes, apply this profile"
        handleConfirm={confirmProfileChange}
        dataTestId='profile-replace-dialog'
      />

    </RestaurantAdminContainer>
  );
}
